import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    Stack,
    Chip,
    InputAdornment,
} from '@mui/material';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import smartPayLogo from '../assets/brand/smartpay-logo.svg';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { setAuth } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post('/login', { username, password });
            setAuth({ ...response.data, username });
            setMessage('Inicio de sesión exitoso.');
            navigate('/dashboard');
        } catch (error) {
            console.error('Error de inicio de sesión', error);
            setMessage('Error al iniciar sesión. Verifica tus credenciales.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(440px, 1.05fr) minmax(480px, .95fr)' }, bgcolor: 'background.paper' }}>
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, position: 'relative', overflow: 'hidden', p: 7, flexDirection: 'column', justifyContent: 'space-between', color: '#FFF', background: 'linear-gradient(145deg, #0E2034 0%, #30296A 62%, #007F77 150%)' }}>
                <Box sx={{ position: 'absolute', width: 430, height: 430, borderRadius: '50%', border: '1px solid rgba(82,228,214,.16)', right: -150, top: -100 }} />
                <Box sx={{ position: 'absolute', width: 270, height: 270, borderRadius: '50%', bgcolor: 'rgba(82,228,214,.07)', left: -80, bottom: 40 }} />
                <Box sx={{ position: 'relative' }}>
                    <Box component="img" src={smartPayLogo} alt="SmartPay" sx={{ width: 255, height: 'auto', display: 'block', ml: -1.5 }} />
                    <Typography variant="caption" sx={{ display: 'block', mt: -4.3, ml: 8.2, color: 'rgba(255,255,255,.55)', letterSpacing: '.14em' }}>PAYMENTS OS</Typography>
                </Box>

                <Box sx={{ position: 'relative', maxWidth: 620 }}>
                    <Chip icon={<BoltRoundedIcon />} label="Operación en tiempo real" sx={{ mb: 3, bgcolor: 'rgba(82,228,214,.12)', color: '#8BECE4', '& .MuiChip-icon': { color: '#52E4D6' } }} />
                    <Typography variant="h2" sx={{ fontSize: { lg: 48, xl: 58 }, lineHeight: 1.07 }}>Pagos simples.<br />Control extraordinario.</Typography>
                    <Typography sx={{ mt: 2.5, maxWidth: 520, fontSize: 17, color: 'rgba(255,255,255,.68)', lineHeight: 1.65 }}>Administra terminales, configura operativas y responde antes a cualquier incidente desde una sola plataforma.</Typography>
                </Box>

                <Stack direction="row" spacing={4} sx={{ position: 'relative' }}>
                    <Box><Typography variant="h5">Una red</Typography><Typography variant="body2" sx={{ color: 'rgba(255,255,255,.55)' }}>Toda tu operación</Typography></Box>
                    <Box><Typography variant="h5">Una verdad</Typography><Typography variant="body2" sx={{ color: 'rgba(255,255,255,.55)' }}>Configuración central</Typography></Box>
                    <Box><Typography variant="h5">Más control</Typography><Typography variant="body2" sx={{ color: 'rgba(255,255,255,.55)' }}>Decisiones rápidas</Typography></Box>
                </Stack>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 2.5, sm: 6, lg: 8 }, py: 5, position: 'relative' }}>
                <Box sx={{ width: '100%', maxWidth: 440 }}>
                    <Box component="img" src={smartPayLogo} alt="SmartPay" sx={{ display: { lg: 'none' }, width: 225, maxWidth: '72vw', height: 'auto', mb: 6, ml: -1.5 }} />
                    <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 800, letterSpacing: '.14em' }}>BIENVENIDO</Typography>
                    <Typography component="h1" variant="h4" sx={{ mt: .5 }}>Accede a tu centro de operación</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, mb: 3.5 }}>Ingresa tus credenciales para continuar.</Typography>

                {message && (
                    <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ width: '100%', mb: 2 }}>
                        {message}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Usuario"
                        name="username"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineRoundedIcon color="action" /></InputAdornment> }}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Contraseña"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon color="action" /></InputAdornment> }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        size="large"
                        sx={{ mt: 3, minHeight: 50 }}
                    >
                        {loading ? 'Ingresando...' : 'Entrar'}
                    </Button>
                </Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 4, color: 'text.secondary' }}><ShieldOutlinedIcon fontSize="small" /><Typography variant="caption">Acceso protegido · SmartPay utiliza conexiones seguras</Typography></Stack>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;
